import React, { useContext, useMemo, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '@/theme/ThemeProvider';

interface TickSizeSelectorProps {
  tickSize: number;
  tickSizeOptions: number[];
  onChange: (tickSize: number) => void;
}

const TickSizeSelector: React.FC<TickSizeSelectorProps> = ({
  tickSize,
  tickSizeOptions,
  onChange,
}) => {
  const { isDarkMode } = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatTickSize = (value: number): string => {
    if (value >= 1) return value.toString();
    return value.toFixed(Math.abs(Math.floor(Math.log10(value))));
  };

  return (
    <Container ref={containerRef}>
      <SelectorButton
        $isDarkMode={isDarkMode}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{formatTickSize(tickSize)}</span>
        <ArrowIcon $isOpen={isOpen}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path
              d="M1 1L5 5L9 1"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ArrowIcon>
      </SelectorButton>

      {isOpen && (
        <Dropdown $isDarkMode={isDarkMode}>
          {tickSizeOptions.map((option) => (
            <DropdownItem
              key={option}
              $isDarkMode={isDarkMode}
              $isActive={option === tickSize}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
            >
              <span>{formatTickSize(option)}</span>
              {option === tickSize && (
                <CheckIcon>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.105 5.872a.9.9 0 011.29 1.256l-10.709 11a.9.9 0 01-1.295-.005l-4.79-5a.9.9 0 011.298-1.246l4.147 4.327 10.06-10.332z" />
                  </svg>
                </CheckIcon>
              )}
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const SelectorButton = styled.button<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid ${({ $isDarkMode }) => ($isDarkMode ? '#333' : '#e0e0e0')};
  background: ${({ $isDarkMode }) => ($isDarkMode ? '#1F1F1F' : '#fff')};
  color: ${({ $isDarkMode }) => ($isDarkMode ? '#fff' : '#333')};
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ $isDarkMode }) => ($isDarkMode ? '#555' : '#bbb')};
    background: ${({ $isDarkMode }) => ($isDarkMode ? '#2a2a2a' : '#f5f5f5')};
  }
`;

const ArrowIcon = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const Dropdown = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 100px;
  padding: 4px 0;
  border-radius: 6px;
  border: 1px solid ${({ $isDarkMode }) => ($isDarkMode ? '#333' : '#e0e0e0')};
  background: ${({ $isDarkMode }) => ($isDarkMode ? '#1F1F1F' : '#fff')};
  box-shadow: 0 4px 12px rgba(0, 0, 0, ${({ $isDarkMode }) => ($isDarkMode ? 0.4 : 0.15)});
  z-index: 1000;
`;

const DropdownItem = styled.div<{ $isDarkMode: boolean; $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: 12px;
  color: ${({ $isDarkMode }) => ($isDarkMode ? '#fff' : '#333')};
  cursor: pointer;
  transition: background 0.15s ease;

  ${({ $isActive, $isDarkMode }) =>
    $isActive &&
    `
    background: ${$isDarkMode ? '#2a2a2a' : '#f0f0f0'};
  `}

  &:hover {
    background: ${({ $isDarkMode }) => ($isDarkMode ? '#333' : '#f5f5f5')};
  }
`;

const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  color: #0ECB81;
`;

export default React.memo(TickSizeSelector);
